<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

// Event data
const event = ref<any>(null)
const coaches = ref<any[]>([])
const players = ref<any[]>([])

// Attendance state - all checked by default
const coachAttendance = ref<Record<string, { present: boolean, reason: string }>>({})
const playerAttendance = ref<Record<string, { present: boolean, reason: string }>>({})

// Loading states
const loading = ref(true)
const submitting = ref(false)

// Get event UUID and date from route
const eventUuid = computed(() => route.params.uuid as string)
const eventDate = computed(() => route.query.date as string || '')
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Computed counts
const presentCoachesCount = computed(() => {
  return Object.values(coachAttendance.value).filter(a => a.present).length
})

const absentCoachesCount = computed(() => {
  return Object.values(coachAttendance.value).filter(a => !a.present).length
})

const presentPlayersCount = computed(() => {
  return Object.values(playerAttendance.value).filter(a => a.present).length
})

const absentPlayersCount = computed(() => {
  return Object.values(playerAttendance.value).filter(a => !a.present).length
})

// Check if all coaches are checked
const allCoachesChecked = computed(() => {
  return Object.values(coachAttendance.value).every(a => a.present)
})

// Check if all players are checked
const allPlayersChecked = computed(() => {
  return Object.values(playerAttendance.value).every(a => a.present)
})

// Fetch event and participants
const fetchEventAndParticipants = async () => {
  if (!clubUuid.value || !eventUuid.value) return

  loading.value = true
  try {
    // Fetch event details (with date parameter if provided)
    const eventResponse = await eventService.getEvent(clubUuid.value, eventUuid.value, eventDate.value)
    event.value = eventResponse

    // Fetch participants
    const participantsResponse = await eventService.getParticipants(clubUuid.value, eventUuid.value, eventDate.value)
    coaches.value = participantsResponse.coaches || []
    players.value = participantsResponse.players || []

    // Initialize attendance state - all present by default
    coaches.value.forEach(coach => {
      coachAttendance.value[coach.uuid] = { present: true, reason: '' }
    })

    players.value.forEach(player => {
      playerAttendance.value[player.uuid] = { present: true, reason: '' }
    })

  } catch (err: any) {
    console.error('Error fetching event/participants:', err)

    // If 404, redirect to events list
    if (err.response?.status === 404) {
      toastStore.error(err.response?.data?.message || t('addAttendance.errorFetchingParticipants'))
      router.push('/events')
      return
    }

    toastStore.error(err.response?.data?.message || t('addAttendance.errorFetchingParticipants'))
  } finally {
    loading.value = false
  }
}

// Toggle attendance for a person
const toggleAttendance = (uuid: string, type: 'coach' | 'player') => {
  if (type === 'coach') {
    coachAttendance.value[uuid].present = !coachAttendance.value[uuid].present
    if (coachAttendance.value[uuid].present) {
      coachAttendance.value[uuid].reason = ''
    }
  } else {
    playerAttendance.value[uuid].present = !playerAttendance.value[uuid].present
    if (playerAttendance.value[uuid].present) {
      playerAttendance.value[uuid].reason = ''
    }
  }
}

// Toggle all coaches
const toggleAllCoaches = () => {
  const newState = !allCoachesChecked.value
  Object.keys(coachAttendance.value).forEach(uuid => {
    coachAttendance.value[uuid].present = newState
    if (newState) {
      coachAttendance.value[uuid].reason = ''
    }
  })
}

// Toggle all players
const toggleAllPlayers = () => {
  const newState = !allPlayersChecked.value
  Object.keys(playerAttendance.value).forEach(uuid => {
    playerAttendance.value[uuid].present = newState
    if (newState) {
      playerAttendance.value[uuid].reason = ''
    }
  })
}

// Handle form submission
const handleSubmit = async () => {
  if (!clubUuid.value || !eventUuid.value) {
    toastStore.error(t('addAttendance.errorNoClub'))
    return
  }

  submitting.value = true

  try {
    // Prepare attendance data in format expected by backend
    const attendanceData = {
      coaches: Object.entries(coachAttendance.value).map(([uuid, data]) => ({
        coach_uuid: uuid,
        is_present: data.present,
        absence_reason: data.reason || null
      })),
      players: Object.entries(playerAttendance.value).map(([uuid, data]) => ({
        player_uuid: uuid,
        is_present: data.present,
        absence_reason: data.reason || null
      }))
    }

    // Send to API (with date parameter if exists)
    const response = await eventService.saveAttendance(clubUuid.value, eventUuid.value, attendanceData, eventDate.value)

    toastStore.success(t('addAttendance.attendanceSaved'))

    // Use returned event_uuid if available (in case new event was created from cycle)
    const finalEventUuid = response.event_uuid || eventUuid.value

    // Redirect back to event detail (with date parameter if exists)
    const redirectUrl = eventDate.value
      ? `/events/${finalEventUuid}?date=${eventDate.value}`
      : `/events/${finalEventUuid}`
    router.push(redirectUrl)
  } catch (err: any) {
    console.error('Error saving attendance:', err)
    const errorMessage = err.response?.data?.message || t('addAttendance.errorSavingAttendance')
    toastStore.error(errorMessage)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchEventAndParticipants()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link
          :to="eventDate ? `/events/${eventUuid}?date=${eventDate}` : `/events/${eventUuid}`"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('addAttendance.backToEvent') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('addAttendance.title') }}</h1>
        <p v-if="event" class="text-sm text-gray-600 mt-1">{{ event.title }} - {{ event.start }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('addAttendance.loadingParticipants') }}</p>
      </div>

      <!-- Form -->
      <form v-if="!loading" @submit.prevent="handleSubmit">
        <!-- Summary Card -->
        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div class="text-sm text-gray-600">{{ t('addAttendance.presentCoaches') }}</div>
              <div class="text-2xl font-bold text-green-600">{{ presentCoachesCount }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-600">{{ t('addAttendance.absentCoaches') }}</div>
              <div class="text-2xl font-bold text-red-600">{{ absentCoachesCount }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-600">{{ t('addAttendance.presentPlayers') }}</div>
              <div class="text-2xl font-bold text-green-600">{{ presentPlayersCount }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-600">{{ t('addAttendance.absentPlayers') }}</div>
              <div class="text-2xl font-bold text-red-600">{{ absentPlayersCount }}</div>
            </div>
          </div>
        </div>

        <!-- Coaches Section -->
        <div v-if="coaches.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ t('addAttendance.coaches') }}</h2>
            <button
              type="button"
              @click="toggleAllCoaches"
              class="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              {{ allCoachesChecked ? t('addAttendance.uncheckAll') : t('addAttendance.checkAll') }}
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
              v-for="coach in coaches"
              :key="coach.uuid"
              :class="[
                'border-2 rounded p-3 cursor-pointer transition-colors',
                coachAttendance[coach.uuid]?.present
                  ? 'border-green-300 bg-green-50'
                  : 'border-red-300 bg-red-50'
              ]"
              @click="toggleAttendance(coach.uuid, 'coach')"
            >
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  :id="`coach-${coach.uuid}`"
                  v-model="coachAttendance[coach.uuid].present"
                  class="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 pointer-events-none"
                >
                <label :for="`coach-${coach.uuid}`" class="text-sm font-medium text-gray-900 flex-1 cursor-pointer pointer-events-none">
                  {{ coach.first_name }} {{ coach.last_name }}
                </label>
              </div>
              <div v-if="!coachAttendance[coach.uuid]?.present" class="mt-2">
                <input
                  type="text"
                  v-model="coachAttendance[coach.uuid].reason"
                  :placeholder="t('addAttendance.absentReason')"
                  class="w-full px-2 py-1 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                  @click.stop
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Players Section -->
        <div v-if="players.length > 0" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">{{ t('addAttendance.players') }}</h2>
            <button
              type="button"
              @click="toggleAllPlayers"
              class="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              {{ allPlayersChecked ? t('addAttendance.uncheckAll') : t('addAttendance.checkAll') }}
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
              v-for="player in players"
              :key="player.uuid"
              :class="[
                'border-2 rounded p-3 cursor-pointer transition-colors',
                playerAttendance[player.uuid]?.present
                  ? 'border-green-300 bg-green-50'
                  : 'border-red-300 bg-red-50'
              ]"
              @click="toggleAttendance(player.uuid, 'player')"
            >
              <div class="flex items-center gap-2">
                <input
                  type="checkbox"
                  :id="`player-${player.uuid}`"
                  v-model="playerAttendance[player.uuid].present"
                  class="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 pointer-events-none"
                >
                <label :for="`player-${player.uuid}`" class="text-sm font-medium text-gray-900 flex-1 cursor-pointer pointer-events-none">
                  {{ player.first_name }} {{ player.last_name }}
                </label>
              </div>
              <div v-if="!playerAttendance[player.uuid]?.present" class="mt-2">
                <input
                  type="text"
                  v-model="playerAttendance[player.uuid].reason"
                  :placeholder="t('addAttendance.absentReason')"
                  class="w-full px-2 py-1 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                  @click.stop
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end gap-2">
          <router-link
            :to="eventDate ? `/events/${eventUuid}?date=${eventDate}` : `/events/${eventUuid}`"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('addAttendance.cancel') }}
          </router-link>
          <button
            type="submit"
            :disabled="submitting"
            class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ submitting ? t('addAttendance.saving') : t('addAttendance.saveAttendance') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
